import React, { useEffect, useState } from "react";
import {
Form,
Input,
Select,
Button,
DatePicker,
Upload,
message,
Modal,
Spin,
} from "antd";
import { InboxOutlined } from "@ant-design/icons";
import { useDispatch } from "react-redux";
import {
useGetLicensorsPartnersListQuery,
useGetSingleTaxRequestQuery,
useAddTaxInvoiceRequestMutation,
useEditTaxInvoiceRequestMutation,
} from "../../appRedux/api/adminPartnerApi";
import moment from "moment";
import "./LicensorAddTaxDocumentModal.css";

const { TextArea } = Input;
const { Dragger } = Upload;
const { Option } = Select;

const formItemLayout = {
    labelCol: {
        xs: { span: 24 },
        sm: { span: 5 },
    },
    wrapperCol: {
        xs: { span: 24 },
        sm: { span: 18 },
    },
    };

    // Constants for validation
    const MAX_FILE_SIZE = 15 * 1024 * 1024; // 15MB in bytes

    const LicensorAddTaxDocumentModal = ({ visible, onCancel, onSuccess, editId }) => {
    const [editFlag, setEditFlag] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [editedFields, setEditedFields] = useState({});
    const [removedFiles, setRemovedFiles] = useState([]); // Track files to be deleted
    const [initialFiles, setInitialFiles] = useState([]); // Track initial files for edit mode

    const dispatch = useDispatch();
    const [form] = Form.useForm();

    // RTK Query hooks
    const { data: royaltyPartnersList = [], isLoading: partnersLoading } = useGetLicensorsPartnersListQuery(
        undefined,
        { skip: !visible }
    );
    
    const { data: editTaxRequestData, isLoading: editDataLoading } = useGetSingleTaxRequestQuery(
        editId,
        { skip: !editId }
    );

    const [addTaxInvoiceRequest] = useAddTaxInvoiceRequestMutation();
    const [editTaxInvoiceRequest] = useEditTaxInvoiceRequestMutation();

    const handleFieldChange = (changedValues) => {
        setEditedFields((prevEditedFields) => ({
        ...prevEditedFields,
        ...changedValues,
        }));
    };

    useEffect(() => {
        if (editId) {
        setEditFlag(true);
        } else {
        setEditFlag(false);
        }
    }, [editId]);

    useEffect(() => {
        if (editFlag && editTaxRequestData && visible) {
        // Set initial files for edit mode
        const initialFiles = editTaxRequestData?.invoice_file?.map((file) => ({
            uid: file.id,
            name: file.document.split("/").pop(),
            status: "done",
            url: file.document,
            documentId: file.id, // Store document ID for deletion tracking
        })) || [];
        
        setInitialFiles(initialFiles);
        
        form.setFieldsValue({
            partner: editTaxRequestData?.partner_id?.partner_id,
            year: editTaxRequestData?.year
            ? moment(`${editTaxRequestData?.year}-01-01T00:00:00.000Z`)
            : null,
            tax_invoice: initialFiles,
        });
        } else if (!editFlag && visible) {
        // Reset for add mode
        form.resetFields();
        setRemovedFiles([]);
        setInitialFiles([]);
        }
    }, [editFlag, editTaxRequestData, form, visible]);

    const handleRemove = (file) => {
        // If file has a documentId (existing file), add to removedFiles for deletion
        if (file.documentId) {
        setRemovedFiles(prev => [...prev, file.documentId]);
        }
        return true; // Allow removal from UI
    };

    const normFile = (e) => {
        if (Array.isArray(e)) {
        return e;
        }
        return e && e.fileList;
    };

    // Function to count new files (files with originFileObj)
    const countNewFiles = (fileList) => {
        return fileList.filter(file => file.originFileObj).length;
    };

    const checkFile = (file, fileList) => {
        const allowedFileTypes = [
        "application/pdf",
        "image/jpeg",
        "image/jpg",
        "image/png",
        ];
        const isTypeValid = allowedFileTypes.includes(file.type);
        const isSizeValid = file.size <= MAX_FILE_SIZE;

        if (!isTypeValid) {
        message.error("Please upload a PDF, JPEG, or PNG document!");
        return Upload.LIST_IGNORE;
        } else if (!isSizeValid) {
        message.error("File size must be less than or equal to 15 MB!");
        return Upload.LIST_IGNORE;
        }

        // Return false to prevent automatic upload
        return false;
    };

    const onFinish = async (values) => {
        setIsSubmitting(true);
        try {
        const formData = new FormData();
        
        // Final validation before submission
        const fileList = values.tax_invoice || [];
        const newFileCount = countNewFiles(fileList);

        // For new requests, ensure at least one file is uploaded
        if (!editFlag && newFileCount === 0) {
            message.error("Please upload at least one tax document");
            setIsSubmitting(false);
            return;
        }

        // For edit mode, if all files are removed, show error
        if (editFlag && fileList.length === 0) {
            message.error("Please upload at least one tax document");
            setIsSubmitting(false);
            return;
        }

        if (!editFlag) {
            // Add new tax document
            values.tax_invoice.forEach((data) => {
            if (data.originFileObj) {
                formData.append("tax_invoice", data.originFileObj);
            }
            });
            formData.append("year", moment(values.year.toISOString())?.year());
            formData.append("partner_id", values.partner);
            
            await addTaxInvoiceRequest(formData).unwrap();
        } else {
            // Edit existing tax document
            
            // Handle file uploads
            const currentFiles = values.tax_invoice || [];
            const newFiles = currentFiles.filter(file => file.originFileObj);
            newFiles.forEach(file => {
            formData.append("tax_invoice", file.originFileObj);
            });

            // Add other fields
            formData.append("year", moment(values.year.toISOString())?.year());
            formData.append("partner_id", values.partner);
            
            // Add files_to_delete parameter for removed files (comma-separated)
            if (removedFiles.length > 0) {
            formData.append("files_to_delete", removedFiles.join(','));
            }
            
            formData.append("status", "PENDING");

            await editTaxInvoiceRequest({ id: editId, data: formData }).unwrap();
        }
        
        onSuccess();
        handleCancel();
        } catch (error) {
        console.error("Error saving tax document:", error);
        message.error(
            error?.data?.message || 
            `Failed to ${editFlag ? 'update' : 'submit'} tax document. Please try again.`
        );
        } finally {
        setIsSubmitting(false);
        }
    };

    const handleCancel = () => {
        form.resetFields();
        setEditedFields({});
        setRemovedFiles([]); // Reset removed files on cancel
        setInitialFiles([]); // Reset initial files on cancel
        setIsSubmitting(false);
        onCancel();
    };

    const isLoading = isSubmitting || partnersLoading || editDataLoading;

    return (
        <Modal
        title={null}
        visible={visible}
        onCancel={handleCancel}
        footer={null}
        width={600}
        centered
        destroyOnClose
        className="add-tax-document-modal"
        >
        <div className="modal-header">
            <h2>{editFlag ? "Edit Tax Document" : "Add Tax Document"}</h2>
        </div>

        <Spin spinning={isLoading}>
            <Form
            {...formItemLayout}
            form={form}
            name="add-tax-document"
            onValuesChange={handleFieldChange}
            onFinish={onFinish}
            disabled={isLoading}
            scrollToFirstError
            >
            <Form.Item
                label="Year"
                name="year"
                rules={[
                {
                    required: true,
                    message: "Please select a year!",
                },
                ]}
            >
                <DatePicker picker="year" style={{ width: "100%" }} />
            </Form.Item>

            <Form.Item
                label="Partner"
                name="partner"
                rules={[
                {
                    required: true,
                    message: "Please select a partner!",
                },
                ]}
            >
                <Select
                placeholder="Select a partner"
                loading={partnersLoading}
                >
                {/* When editing, ensure the partner from editTaxRequestData is available and shown */}
                {editFlag && editTaxRequestData?.partner_id ? (
                    <Option key={editTaxRequestData.partner_id.partner_id} value={editTaxRequestData.partner_id.partner_id}>
                    {editTaxRequestData.partner_id.company_name}
                    </Option>
                ) : (
                    Array.isArray(royaltyPartnersList) && royaltyPartnersList.map((partner) => (
                    <Option
                        key={partner.partner_id}
                        value={partner.partner_id}
                    >
                        {partner.company_name}
                    </Option>
                    ))
                )}
                </Select>
            </Form.Item>

            <Form.Item
                label="Document"
                name="tax_invoice"
                valuePropName="fileList"
                getValueFromEvent={normFile}
                rules={[
                { 
                    required: true, 
                    message: "Please upload at least one tax document" 
                },
                ({ getFieldValue }) => ({
                    validator(_, value) {
                    const fileList = value || [];
                    
                    if (fileList.length === 0) {
                        return Promise.reject(new Error("Please upload at least one tax document"));
                    }

                    // Check each file
                    for (const file of fileList) {
                        // Only validate new files (those with originFileObj)
                        if (file.originFileObj) {
                        const allowedFileTypes = [
                            "application/pdf",
                            "image/jpeg",
                            "image/jpg",
                            "image/png",
                        ];
                        
                        if (!allowedFileTypes.includes(file.originFileObj.type)) {
                            return Promise.reject(
                            new Error(`Invalid file type for ${file.name}. Please upload PDF, JPEG, or PNG documents only!`)
                            );
                        }

                        if (file.originFileObj.size > MAX_FILE_SIZE) {
                            return Promise.reject(
                            new Error(`File ${file.name} exceeds 15MB size limit!`)
                            );
                        }
                        }
                    }

                    return Promise.resolve();
                    },
                }),
                ]}
            >
                <Dragger
                multiple={true}
                beforeUpload={(file, fileList) => {
                    return checkFile(file, fileList);
                }}
                accept=".pdf, .png, .jpeg, .jpg"
                onRemove={handleRemove}
                defaultFileList={editFlag ? initialFiles : []}
                listType="text"
                >
                <p className="ant-upload-drag-icon">
                    <InboxOutlined />
                </p>
                <p className="ant-upload-text">
                    Click or drag files to this area to upload
                </p>
                <p className="ant-upload-hint">
                    Support for a single or bulk upload (pdf, png, jpeg). 
                    Max file size: 15MB per file.
                </p>
                </Dragger>
            </Form.Item>

            <div className="modal-footer-btns">
                <Button
                className="cancel-btn"
                onClick={handleCancel}
                disabled={isLoading}
                >
                Cancel
                </Button>
                <Button
                type="primary"
                htmlType="submit"
                loading={isSubmitting}
                className="submit-btn"
                disabled={isLoading}
                >
                {editFlag ? "Save" : "Submit"}
                </Button>
            </div>
            </Form>
        </Spin>
        </Modal>
    );
};

export default LicensorAddTaxDocumentModal;